import React, { useState, useEffect, useRef } from 'react';
import Dropzone from 'react-dropzone';
import { Button, Card, Col, Row } from 'reactstrap';
import { formatBytes } from './formatbytes';
import { getIn } from 'formik';
import FeatherIcon from "feather-icons-react";
import axios from '../../../api/axios';
import { api } from './constants';

const { upload_documents_temp_url: UPLOAD_DOCUMENTS_TEMP_URL } = api.endpoints;

const FileUploadField = ({ name, label, formik }) => {
    const [isDragActive, setIsDragActive] = useState(false);
    const [localFiles, setLocalFiles] = useState([]);
    const initializedRef = useRef(false);
    const localFilesRef = useRef([]);

    useEffect(() => {
        localFilesRef.current = localFiles;
    }, [localFiles]);

    useEffect(() => {
        if (initializedRef.current) return;
        const currentVal = getIn(formik?.values, name);
        if (Array.isArray(currentVal) && currentVal.length > 0) {
            setLocalFiles(
                currentVal.map((p, i) => ({
                    id: `restore-${Date.now()}-${i}`,
                    name: p.name,
                    preview: p.preview,
                    formattedSize: p.formattedSize,
                    status: 'uploaded',
                    url: p.url,
                }))
            );
        }
        initializedRef.current = true;
    }, [formik, name]);

    useEffect(() => {
        return () => {
            localFilesRef.current.forEach((f) => {
                if (f.preview && typeof f.preview === 'string' && f.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(f.preview);
                }
            });
        };
    }, []);

    if (!formik) return null;

    const { setFieldValue, setFieldTouched, touched, errors } = formik;

    const syncFormik = (next) => {
        const uploaded = next
            .filter((f) => f.status === 'uploaded' && f.url)
            .map((f) => ({
                url: f.url,
                preview: f.preview,
                formattedSize: f.formattedSize,
                name: f.name,
            }));
        setFieldValue(name, uploaded);
    };

    const uploadOne = async (file, id) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axios.post(UPLOAD_DOCUMENTS_TEMP_URL, formData, {
                headers: { 'content-type': 'multipart/form-data' },
            });

            const url = data?.urls?.[0];
            if (!url) throw new Error(data?.message || 'Upload did not return a URL');

            setLocalFiles((prev) => {
                const next = prev.map((f) =>
                    f.id === id ? { ...f, status: 'uploaded', url } : f
                );
                syncFormik(next);
                return next;
            });
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Upload failed. Please try again.';
            setLocalFiles((prev) =>
                prev.map((f) => (f.id === id ? { ...f, status: 'error', errorMessage: message } : f))
            );
        }
    };

    const handleAcceptedFiles = async (acceptedFiles) => {
        setIsDragActive(false);
        setFieldTouched(name, true);

        const incoming = acceptedFiles.map((file) => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: file.name,
            preview: URL.createObjectURL(file),
            formattedSize: formatBytes(file.size),
            status: 'uploading',
        }));

        setLocalFiles((prev) => [...prev, ...incoming]);

        await Promise.all(acceptedFiles.map((file, i) => uploadOne(file, incoming[i].id)));
    };

    const handleRemoveFile = (id) => {
        setLocalFiles((prev) => {
            const target = prev.find((f) => f.id === id);
            if (target?.preview && typeof target.preview === 'string' && target.preview.startsWith('blob:')) {
                URL.revokeObjectURL(target.preview);
            }
            const next = prev.filter((f) => f.id !== id);
            syncFormik(next);
            return next;
        });
    };

    const meta = {
        touched: getIn(touched, name),
        error: getIn(errors, name),
    };

    const hasUploading = localFiles.some((f) => f.status === 'uploading');

    return (
        <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center w-100">
                {label}
            </label>

            <Dropzone
                onDrop={handleAcceptedFiles}
                onDragEnter={() => setIsDragActive(true)}
                onDragLeave={() => setIsDragActive(false)}
            >
                {({ getRootProps, getInputProps }) => (
                    <div
                        {...getRootProps()}
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ease-in-out
                            ${isDragActive ? 'border-blue-500 bg-blue-50 scale-105' : 'border-gray-300 hover:border-blue-400'}
                            ${meta.touched && meta.error ? 'border-red-500' : ''}`}
                    >
                        <input {...getInputProps()} accept=".jpg,.jpeg,.png" />
                        <div className={`${localFiles.length > 0 ? 'd-none' : ''}`}>
                            <svg
                                className={`
                                    mx-auto h-16 w-16
                                    transition-colors duration-300
                                    ${isDragActive ? 'text-blue-500' : 'text-gray-400'}
                                `}
                                width={120}
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                            >
                                <path
                                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        <p className={`
                            text-sm transition-colors duration-300
                            ${isDragActive ? 'text-blue-600' : 'text-gray-600'}
                        `}>
                            {isDragActive
                                ? 'Drop files here'
                                : 'Drag and drop files or click to upload'}
                        </p>
                    </div>
                )}
            </Dropzone>

            <div className="dropzone-previews mt-3" id="file-previews">
                {localFiles.length > 0 && localFiles.map((f) => (
                    <Card className="mt-1 mb-0 shadow-sm border dz-processing dz-image-preview dz-success dz-complete" key={f.id}>
                        <div className="p-2">
                            <Row className="align-items-center">
                                <Col className="col-auto">
                                    <img
                                        data-dz-thumbnail=""
                                        height="80"
                                        className="avatar-sm rounded bg-light"
                                        alt={f.name}
                                        src={f.preview}
                                        style={f.status !== 'uploaded' ? { opacity: 0.6 } : undefined}
                                    />
                                </Col>
                                <Col>
                                    <div className="text-muted font-weight-bold">{f.name}</div>
                                    <p className="mb-0">
                                        <strong>{f.formattedSize}</strong>
                                        {f.status === 'uploading' && (
                                            <span className="ms-2 text-primary">• Uploading…</span>
                                        )}
                                        {f.status === 'error' && (
                                            <span className="ms-2 text-danger">• {f.errorMessage || 'Upload failed'}</span>
                                        )}
                                    </p>
                                </Col>
                                <Col className="col-auto">
                                    <Button color="danger ms-2" size="sm" onClick={() => handleRemoveFile(f.id)}>
                                        <FeatherIcon icon="trash-2" size={16} />
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    </Card>
                ))}
            </div>

            {hasUploading && (
                <div className="text-primary text-sm mt-2 text-center">
                    Uploading documents… please wait before submitting.
                </div>
            )}

            {meta.touched && meta.error && (
                <div className="text-red-500 text-sm bg-red-50 p-2 row text-center w-100 flex items-center space-x-2">
                    <div className="text-danger">{meta.error}
                        <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20" width={20}>
                            <path fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </div>
                </div>
            )}
        </div>
    );
};

export { FileUploadField };
